import { Form as FinalForm } from 'react-final-form'
import { useMutation } from '@apollo/client'
import Bowser from 'bowser'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import Field from '@/components/Field'
import Button from '@/ui/Button'
import Form from '@/ui/Form'
import FlexRow from '@/ui/FlexRow'
import Text from '@/ui/Text'
import TextArea from '@/ui/TextArea'
import { required } from '@/helpers/validators'
import { CREATE_ERROR_REPORT } from '@/graphql/mutations/ErrorReport'

interface BugReportFormProps {
  errorVariables: {
    error: string
    routeName: string
    userAgent: Bowser.Parser.ParsedResult
  }
}

interface FormValues {
  message: string
  grantAccess: boolean
}

const BugReportForm = ({ errorVariables }: BugReportFormProps) => {
  const { t } = useTranslation()
  const [submitted, setSubmitted] = useState(false)

  const [createErrorReport] = useMutation(CREATE_ERROR_REPORT)

  const handleSubmit = async ({ message, grantAccess }: FormValues) => {
    await createErrorReport({
      variables: {
        ...errorVariables,
        useEmail: true,
        isCrash: true,
        message,
        grantAccess,
      },
    })

    setSubmitted(true)
  }

  if (submitted) {
    return (
      <div style={{ textAlign: 'left' }}>
        <Text.H2>{t('Thank you for your report')}</Text.H2>
        <Text.P>
          {t('We will investigate the issue as soon as possible')}
        </Text.P>
      </div>
    )
  }

  return (
    <div>
      <Text.H1>{t('Something went wrong')}</Text.H1>

      <div style={{ textAlign: 'left' }}>
        <Text.H2>{t('Send an error report if the problem persists')}:</Text.H2>

        <FinalForm
          onSubmit={handleSubmit}
          validate={required(['message'])}
          render={({ invalid, submitting, handleSubmit }) => (
            <Form loading={submitting}>
              <Field as={TextArea} label={t('Error report')} name="message" />

              <Field
                as={Form.Checkbox}
                label={t(
                  'I grant permission to HR-ON to temporarily access my data to investigate this issue.',
                )}
                name="grantAccess"
              />

              <FlexRow noMargin justifyContent="flex-end">
                <Button
                  onClick={handleSubmit}
                  disabled={invalid}
                  content={t('Send error report')}
                />
              </FlexRow>
            </Form>
          )}
        />
      </div>
    </div>
  )
}

export default BugReportForm
